import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import type { AiCreditPackOption } from "./creditPacks";

/**
 * GAU pack dropdown for seatless checkout surfaces (credit-card gate, AI
 * quota paywall). Mirrors the Manage Plan modal's credit-pack dropdown
 * styling (trigger chrome, menu card, hover/selected states) so pack
 * selection looks the same everywhere — a native <select> renders the OS
 * menu, which doesn't match the platform.
 *
 * The menu PORTALS to document.body with fixed positioning: the paywall
 * renders this picker inside a card with `overflow: hidden` (and the modal
 * body is its own scroll container), so an in-container absolute menu gets
 * clipped after a few options.
 */
export function CreditPackSelect({
  options,
  selectedCredits,
  onSelect,
  billingInterval,
  disabled = false,
  dataTest = "credit-pack-select",
}: {
  options: AiCreditPackOption[];
  selectedCredits: number;
  onSelect: (credits: number) => void;
  billingInterval: "annual" | "monthly";
  /** Blocks opening/selection — e.g. while the committed-pack default is still loading. */
  disabled?: boolean;
  dataTest?: string;
}) {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const updateMenuPosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  // Compute position in a LAYOUT effect (before paint) so the portaled menu never
  // renders a frame at a stale/absent position: `open && menuPosition` flips to true
  // in the same commit. Clearing on close forces a fresh recompute on the next open
  // (listeners are detached while closed, so a previously-captured position can go stale).
  useLayoutEffect(() => {
    if (!open) {
      setMenuPosition(null);
      return;
    }
    updateMenuPosition();
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    window.addEventListener("resize", updateMenuPosition);
    // Capture-phase so scrolls of ANY ancestor container (the modal body is
    // its own scroll context) keep the fixed-position menu glued to the trigger.
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  const intervalSuffix = billingInterval === "annual" ? "/yr" : "/mo";
  const formatLabel = (option: AiCreditPackOption) =>
    `${option.credits.toLocaleString()} GAUs — $${option.priceUsd.toLocaleString()}${intervalSuffix}`;
  const selected =
    options.find((option) => option.credits === selectedCredits) ?? options[0];

  return (
    <div className="relative w-full">
      <button
        ref={triggerRef}
        type="button"
        data-test={dataTest}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex h-10 w-full items-center gap-1 rounded-[6px] border-0 bg-white px-3 py-2 text-left shadow-[0_0_0_1px_rgba(31,38,51,0.12)] transition ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-[#f9fafb]"}`}
      >
        <span className="flex-1 truncate text-sm leading-4 text-[#454d5f]">
          {formatLabel(selected)}
        </span>
        <svg
          className={`h-4 w-4 shrink-0 text-[#6c7689] transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>
      {open && menuPosition
        ? createPortal(
            <div
              ref={menuRef}
              role="listbox"
              data-test={`${dataTest}-menu`}
              style={{
                top: menuPosition.top,
                left: menuPosition.left,
                width: menuPosition.width,
              }}
              className="fixed z-[2000] max-h-[320px] overflow-y-auto overflow-x-hidden rounded-lg border border-[#e8eaed] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.12)]"
            >
              {options.map((option) => {
                const isSelected = option.credits === selected.credits;
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-test={`${dataTest}-option-${option.credits}`}
                    onClick={() => {
                      onSelect(option.credits);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center justify-between border-0 px-3 py-2.5 text-left text-sm transition ${isSelected ? "cursor-pointer bg-[#f9fafb] text-[#1f2633]" : "cursor-pointer bg-white text-[#1f2633] hover:bg-[#f9fafb]"}`}
                  >
                    <span>{formatLabel(option)}</span>
                    <span
                      className={`text-sm leading-none ${isSelected ? "text-[#1f2633]" : "text-transparent"}`}
                    >
                      ✓
                    </span>
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
